/**
 * GradeCard — public-facing AI card grading page.
 *
 * Layout (top → bottom):
 *   1. GradeHero            — strong commercial positioning + primary CTA
 *   2. StatsBar             — live counters (graded total / this week / etc.)
 *   3. Scanner section      — the actual capture + report flow
 *   4. HowItWorks           — 4-step demo mockup, anchor #how-it-works
 *   5. ShowcaseGallery      — admin-curated sample reports
 *   6. GradeFAQ             — credibility / objection-handling
 *   7. GradeCtaStrip        — repeat conversion CTA
 *
 * Routes:
 *   /grade                  — the full landing + scanner
 *   /grade/share/:token     — a single shared report (skips the landing)
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CardScanner from '../components/grading/CardScanner';
import GradingReport from '../components/grading/GradingReport';
import IdentifyPanel from '../components/grading/IdentifyPanel';
import HowItWorks from '../components/grading/HowItWorks';
import GradeHero from '../components/grading/GradeHero';
import StatsBar from '../components/grading/StatsBar';
import ShowcaseGallery from '../components/grading/ShowcaseGallery';
import GradeFAQ from '../components/grading/GradeFAQ';
import GradeCtaStrip from '../components/grading/GradeCtaStrip';
import '../components/grading/grading.css';

const FIELD_NAMES = ['front', 'back', 'frontTilt1', 'frontTilt2', 'backTilt1', 'backTilt2'];

export default function GradeCard() {
  const { token } = useParams();
  const [mode, setMode] = useState('frontBack'); // 'front' | 'frontBack'
  const [busy, setBusy]       = useState(false);
  const [report, setReport]   = useState(null);
  const [error, setError]     = useState(null);
  const [meta, setMeta] = useState({ cardName: '', tcgCardId: '', rawValue: '', currency: 'USD' });
  const [latestFront, setLatestFront] = useState(null);
  const [stats, setStats] = useState(null);

  const scannerRef = useRef(null);
  const scrollToScanner = () => {
    const el = scannerRef.current;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Share-view: load on mount.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/grading/share/${encodeURIComponent(token)}`);
        if (!r.ok) throw new Error((await r.json()).error || `HTTP ${r.status}`);
        const data = await r.json();
        if (alive) setReport(data);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load report');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Public stats for the hero + stats bar.
  useEffect(() => {
    if (token) return;
    let alive = true;
    fetch('/api/grading/stats/public')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (alive && data) setStats(data); })
      .catch(() => { /* ignore — UI shows '—' */ });
    return () => { alive = false; };
  }, [token]);

  const handleSubmit = async (captured) => {
    setBusy(true); setError(null); setReport(null);
    try {
      const fd = new FormData();
      for (const name of FIELD_NAMES) {
        if (captured[name]) fd.append(name, captured[name], `${name}.jpg`);
      }
      if (meta.cardName)  fd.append('cardName',  meta.cardName);
      if (meta.tcgCardId) fd.append('tcgCardId', meta.tcgCardId);
      if (meta.rawValue)  fd.append('rawValue',  String(Number(meta.rawValue) || 0));
      if (meta.currency)  fd.append('currency',  meta.currency);
      const tokenAuth = localStorage.getItem('userToken') || '';
      const res = await fetch('/api/grading/submit', {
        method: 'POST',
        body: fd,
        headers: tokenAuth ? { Authorization: `Bearer ${tokenAuth}` } : {},
      });
      const data = await res.json();
      if (!res.ok) {
        // Sprint 6: friendlier copy for the common failure modes.
        if (res.status === 429) {
          const loggedIn = !!localStorage.getItem('userToken');
          throw new Error(loggedIn
            ? "You've hit today's grading limit (20 / hour). Try again in a bit, or contact us for higher limits."
            : "You've hit the anonymous grading limit (5 / hour). Sign in to bump it to 20 / hour.");
        }
        if (res.status === 413) throw new Error('Photos are too large. Try recapturing — each image should be under 12 MB.');
        if (res.status >= 500) throw new Error('Grading service is having trouble. Please retry in a moment.');
        throw new Error(data.error || `Grading failed (HTTP ${res.status}).`);
      }
      setReport({ ...data, stageVersion: data.stageVersion || 'v2-full', createdAt: new Date().toISOString() });
      // Scroll the report into view after a tick so the user sees results.
      setTimeout(() => window.scrollTo({ top: scannerRef.current?.offsetTop || 0, behavior: 'smooth' }), 50);
    } catch (e) {
      setError(e.message || 'Grading failed.');
    } finally {
      setBusy(false);
    }
  };

  // ---- Share-view (single report) ----
  if (token) {
    if (error) return <div className="gp-page"><ErrorBox msg={error} /></div>;
    if (!report) return <div className="gp-page"><p className="gp-section">Loading shared report…</p></div>;
    const ogTitle = `${report.cardName || 'Card'} — AI grade ${report.overall ?? '–'}/10`;
    const ogDesc = describeReport(report);
    const ogImage = report.front?.imageUrl ? new URL(report.front.imageUrl, window.location.origin).toString() : null;
    return (
      <div className="gp-page">
        <Helmet>
          <title>{ogTitle} · SilverGames</title>
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDesc} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={window.location.href} />
          {ogImage && <meta property="og:image" content={ogImage} />}
          <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={ogTitle} />
          <meta name="twitter:description" content={ogDesc} />
          {ogImage && <meta name="twitter:image" content={ogImage} />}
        </Helmet>
        <GradingReport report={report} />
        <GradeCtaStrip onStart={() => { window.location.href = '/grade'; }} />
      </div>
    );
  }

  // ---- Landing + scanner ----
  return (
    <div className="gp-page">
      <GradeHero stats={stats} onStart={scrollToScanner} />

      <StatsBar stats={stats} />

      <div style={{ textAlign: 'center', margin: '8px 0' }}>
        <a href="/grade/accuracy" style={{ fontSize: 14, fontWeight: 600 }}>
          How accurate is our AI grade? See verified results →
        </a>
      </div>

      <section className="gp-section" ref={scannerRef}>
        {error && <ErrorBox msg={error} />}

        {!report ? (
          <>
            <span className="gp-eyebrow">Grade now</span>
            <h2 className="gp-h2">Frame your card and let the camera do the rest</h2>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <ModeBtn current={mode} value="front"     onChange={setMode}>Front only</ModeBtn>
              <ModeBtn current={mode} value="frontBack" onChange={setMode}>Front + back</ModeBtn>
            </div>
            <IdentifyPanel meta={meta} onChange={setMeta} frontImage={latestFront} />
            <CardScanner mode={mode} onSubmit={handleSubmit} onFrontCaptured={setLatestFront} busy={busy} />
          </>
        ) : (
          <>
            <GradingReport report={report} />
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" className="gp-btn gp-btn--primary gp-btn--lg" onClick={() => setReport(null)}>
                Grade another card
              </button>
            </div>
          </>
        )}
      </section>

      <section id="how-it-works" className="gp-section">
        <span className="gp-eyebrow">How it works</span>
        <h2 className="gp-h2">Six photos, four sub-grades, one report.</h2>
        <HowItWorks />
      </section>

      <ShowcaseGallery />

      <GradeFAQ />

      <GradeCtaStrip onStart={scrollToScanner} />
    </div>
  );
}

function ModeBtn({ current, value, onChange, children }) {
  const active = current === value;
  return (
    <button type="button"
      onClick={() => onChange(value)}
      className={`gp-btn ${active ? 'gp-btn--primary' : 'gp-btn--ghost'}`}>
      {children}
    </button>
  );
}

function describeReport(r) {
  const sg = r.subGrades || {};
  const parts = [];
  if (sg.centering != null) parts.push(`centering ${sg.centering}`);
  if (sg.corners   != null) parts.push(`corners ${sg.corners}`);
  if (sg.edges     != null) parts.push(`edges ${sg.edges}`);
  if (sg.surface   != null) parts.push(`surface ${sg.surface}`);
  return `Free AI pre-grade: ${parts.join(', ')}.`;
}

function ErrorBox({ msg }) {
  return (
    <div role="alert" style={{
      background: '#fdecea', color: '#611a15', padding: 12, borderRadius: 6,
      maxWidth: 720, margin: '0 auto 12px',
    }}>
      {msg}
    </div>
  );
}
